import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, BosDurum, Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, acikOturumlar, oturumKapat, type AcikOturum } from "./api";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * AÇIK OTURUMLAR — F-SEC-03
 *
 * ================================================================
 * C40'IN İKİNCİ YARISI
 * ================================================================
 * Mutlak oturum ömrü 30 gün (Emre'nin kararı). O kararın yanına
 * yazılan şart hâlâ geçerli: *"süre uzun tutulduğu için sudo, yeni
 * cihaz uyarısı ve uzaktan sonlandırma artık tercih değil şart."*
 * Bu ekran o üçüncüsü.
 */

/** "3 gün", "5 saat", "12 dakika" — en büyük anlamlı birim. */
export function gecenSure(iso: string, simdi = new Date()): string {
  const fark = simdi.getTime() - new Date(iso).getTime();
  if (fark < 0) return "az önce";
  const dakika = Math.floor(fark / 60_000);
  if (dakika < 1) return "az önce";
  if (dakika < 60) return `${dakika} dakika önce`;
  const saat = Math.floor(dakika / 60);
  if (saat < 24) return `${saat} saat önce`;
  return `${Math.floor(saat / 24)} gün önce`;
}

/**
 * Oturumları KİŞİYE göre grupluyor.
 *
 * ================================================================
 * DÜZ LİSTE YANLIŞ SORUYA CEVAP VERİRDİ
 * ================================================================
 * Bu ekranda sorulan soru "kaç oturum var" değil, **"kimin kaç
 * cihazı bağlı"**. Düz bir liste, aynı kişinin altı oturumunu altı
 * ayrı satır olarak gösterir ve tek bir kişide biriken anormalliği
 * görünmez kılardı — oysa aranan şey tam olarak o.
 *
 * Sıralama: en çok oturumu olan başta, eşitlikte en son görülen.
 */
export function kisiyeGoreGrupla(
  oturumlar: readonly AcikOturum[],
): Array<{ userId: string; adSoyad: string; email: string | null; oturumlar: AcikOturum[] }> {
  const kovalar = new Map<string, { adSoyad: string; email: string | null; liste: AcikOturum[] }>();
  for (const o of oturumlar) {
    const kova = kovalar.get(o.userId);
    if (kova) kova.liste.push(o);
    else kovalar.set(o.userId, { adSoyad: o.adSoyad, email: o.email, liste: [o] });
  }
  return [...kovalar.entries()]
    .map(([userId, k]) => ({
      userId,
      adSoyad: k.adSoyad,
      email: k.email,
      oturumlar: k.liste,
    }))
    .sort(
      (a, b) =>
        b.oturumlar.length - a.oturumlar.length ||
        new Date(b.oturumlar[0]?.sonGorulme ?? 0).getTime() -
          new Date(a.oturumlar[0]?.sonGorulme ?? 0).getTime(),
    );
}

function OturumSatiri({ oturum }: { oturum: AcikOturum }) {
  const qc = useQueryClient();
  const [gerekce, setGerekce] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);
  const { hataYakala, kapi } = useSudoKapisi("Oturumu kapatmak için şifrenizi doğrulayın.");

  const kapat = useMutation({
    mutationFn: () => oturumKapat(oturum.id, gerekce ?? ""),
    onError: hataYakala(() => {
      if (gerekce !== null) kapat.mutate();
    }),
    onSuccess: async (s) => {
      setSonuc(
        s.kapatildi
          ? "Oturum kapatıldı. Kullanıcı bir sonraki isteğinde çıkışa düşer."
          : "Oturum zaten kapalıymış — liste eskimişti.",
      );
      setGerekce(null);
      await qc.invalidateQueries({ queryKey: ["guvenlik-oturumlar"] });
    },
  });

  return (
    <div className="border-notr-100 flex flex-col gap-2 border-b py-2 last:border-b-0">
      {kapi}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-notr-800 text-sm">
            {oturum.cihaz ?? "Cihaz kaydedilmemiş"}
            {oturum.guvenilir && (
              <span className="ml-2">
                <Rozet ton="bilgi">güvenilir cihaz</Rozet>
              </span>
            )}
          </p>
          <p className="text-notr-600 mt-0.5 text-xs">
            {oturum.ipAddress ?? "IP yok"}
            {oturum.ulke && ` · ${oturum.ulke}`} · son görülme {gecenSure(oturum.sonGorulme)} ·{" "}
            {gecenSure(oturum.basladi)} açıldı
          </p>
        </div>
        {gerekce === null && (
          <Buton gorunum="ikincil" onClick={() => setGerekce("")}>
            Kapat
          </Buton>
        )}
      </div>

      {gerekce !== null && (
        <div className="bg-notr-50 rounded-alan flex flex-col gap-2 p-3">
          <Alan
            etiket="Neden kapatıyorsunuz?"
            yardim="Denetim kaydına yazılır. Başkasının oturumunu kapatmak bir müdahaledir; sebebi sonradan sorulabilir olmalı."
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Buton
              onClick={() => kapat.mutate()}
              yukleniyor={kapat.isPending}
              yukleniyorMetni="Kapatılıyor"
              disabled={gerekce.trim().length === 0}
            >
              Oturumu kapat
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setGerekce(null)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {kapat.isError && (
        /*
          HATA METNİ SUNUCUDAN. Kendi oturumunu kapatmaya çalışan
          yönetici, "Çıkış yap düğmesini kullanın" cümlesini görüyor —
          ekran kendi metnini yazsaydı o yönlendirme kaybolurdu.
        */
        <p role="alert" className="text-hata-700 text-sm">
          {kapat.error instanceof ApiHatasi ? kapat.error.message : "Oturum kapatılamadı."}
        </p>
      )}
      {sonuc && (
        /*
          `role="status"` — SUSU DEGIL, TEK GERI BILDIRIM.
          Ekran okuyucu kullanicisi icin bu kutu islemin bittigini
          soyleyen tek sey; canli bolge tasimazsa dugmeye basiyor ve
          hicbir sey duymuyor. 04.09.2026 olcumu: yirmi geri bildirim
          kutusundan ucu sessizdi ve `geri-bildirim-duyurusu.test.ts`
          tam bunun icin yazildi — bu dosyayi da yakaladi.
        */
        <p role="status" className="text-notr-700 text-sm">
          {sonuc}
        </p>
      )}
    </div>
  );
}

export function AcikOturumlar() {
  const liste = useQuery({
    queryKey: ["guvenlik-oturumlar"],
    queryFn: acikOturumlar,
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    return (
      <Kart baslik="Açık oturumlar">
        <BosDurum
          ton="hata"
          baslik="Oturumlar alınamadı"
          aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const gruplar = kisiyeGoreGrupla(liste.data.oturumlar);

  return (
    <Kart
      baslik="Açık oturumlar"
      aciklama={`${liste.data.toplam} açık oturum, ${gruplar.length} kişide. Oturumlar 30 gün açık kalabildiği için uzaktan kapatma bir tercih değil, o kararın şartı.`}
    >
      {gruplar.length === 0 ? (
        <p className="text-notr-700 text-sm">Şu an açık oturum yok.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {gruplar.map((g) => (
            <div key={g.userId}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-notr-900 text-sm font-medium">{g.adSoyad}</span>
                <span className="text-notr-600 text-xs">{g.email ?? "e-posta yok"}</span>
                {g.oturumlar.length >= 4 && (
                  /*
                    DÖRT VE ÜSTÜ İŞARETLİ. Bir kişide biriken oturum,
                    tek başına kanıt değil — telefon, tabletle, iki
                    tarayıcı sıradan. Ama dört ve üstü, çıkış yapmadan
                    cihaz değiştiren bir kullanıcıyı ya da kopyalanmış
                    bir çerezi işaret edebilir ve bakmaya değer.
                  */
                  <Rozet ton="uyari">{g.oturumlar.length} açık oturum</Rozet>
                )}
              </div>
              <div className="mt-1 pl-1">
                {g.oturumlar.map((o) => (
                  <OturumSatiri key={o.id} oturum={o} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Kart>
  );
}
